import React, { useState, useEffect } from "react";
import { Menu, X, Edit3, Trash2, ArrowLeft } from "lucide-react";
import axios from "axios";
import { useNavigate } from "react-router-dom";

// Dashboard Layout Components
import DashboardSidebar from "../../components/dashboard components/DashboardSidebar";
import DashboardHeader from "../../components/dashboard components/DashboardHeader";

// Project Form Components
import ProjectHomeTabForm from "../../components/dashboard components/forms/ProjectHomeTabForm";
import ProjectGalleryTabForm from "../../components/dashboard components/forms/ProjectGalleryTabForm";
import ProjectPlanForm from "../../components/dashboard components/forms/ProjectPlanForm";
import ProjectLocationForm from "../../components/dashboard components/forms/ProjectLocationForm";
import ProjectAmenitiesForm from "../../components/dashboard components/forms/ProjectAmenitiesForm";
import ProjectApprovalComplianceForm from "../../components/dashboard components/forms/ProjectApprovalComplianceForm";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

const CompletedProjectPage = () => {
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [activeTab, setActiveTab] = useState("home");
  const [tabMenuOpen, setTabMenuOpen] = useState(false);
  const [selectedProject, setSelectedProject] = useState(null);

  const tabs = [
    { id: "home", label: "Home" },
    { id: "gallery", label: "Gallery" },
    { id: "plan", label: "Project Plan" },
    { id: "location", label: "Location Map" },
    { id: "amenities", label: "Amenities" },
    { id: "approval", label: "Approvals & Compliances" },
  ];

  const activeTabLabel =
    tabs.find((t) => t.id === activeTab)?.label || "Select Section";

  const [projects, setProjects] = useState([]);
  const [homeData, setHomeData] = useState({
    project_name: "",
    project_location: "",
    project_description: "",
    project_rera: "",
    project_status: "",
    project_extent: "",
    project_badge_location: "",
    project_type: "",
    project_price: "",
    project_complete_date: "",
    approval: "",
    hmda: "",
    features: "",
    project_image: "",
    brochure: "",
    gallery_description: "",
    gallery_images: [],
    project_amenities: [],
    project_plan_images: [],
    approvals_compliances: [],
  });

  useEffect(() => {
    const fetchProjects = async () => {
      try {
        const res = await axios.get(
          `${API_BASE_URL}/projects/admin/completed-cards`
        );
        setProjects(Array.isArray(res.data) ? res.data : []);
      } catch (err) {
        console.error("Error fetching completed projects:", err);
      }
    };
    fetchProjects();
  }, []);

  const handleEditProject = async (project) => {
    const id = project.project_id || project.id;
    setSelectedProject({
      ...project,
      name:
        project.project_title ||
        project.project_name ||
        project.name ||
        "Untitled Project",
      id,
    });
    setActiveTab("home");
    setTabMenuOpen(false);

    const parseIfString = (val) => {
      if (!val) return [];
      if (Array.isArray(val)) return val;
      if (typeof val === 'string') {
        try {
          return JSON.parse(val);
        } catch {
          return [];
        }
      }
      return [];
    };

    setHomeData({
      ...project,
      gallery_images: parseIfString(project.gallery_images),
      project_amenities: parseIfString(project.project_amenities),
      project_plan_images: parseIfString(project.project_plan_images),
      approvals_compliances: parseIfString(project.approvals_compliances),
    });
  };

  const handleDeleteProject = async (projectId) => {
    if (!projectId) {
      alert("Invalid project.");
      return;
    }

    const confirmDelete = window.confirm(
      "Are you sure you want to delete this project? This action cannot be undone."
    );
    if (!confirmDelete) return;

    try {
      const token = localStorage.getItem("token");
      await axios.delete(`${API_BASE_URL}/admin/delete-project/${projectId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      alert("Project deleted successfully.");

      setProjects((prev) =>
        prev.filter((p) => (p.project_id || p.id) !== projectId)
      );

      if (selectedProject && selectedProject.id === projectId) {
        setSelectedProject(null);
        setHomeData({
          project_name: "",
          project_location: "",
          project_description: "",
          project_rera: "",
          project_status: "",
          project_extent: "",
          project_badge_location: "",
          project_type: "",
          project_price: "",
          project_complete_date: "",
          approval: "",
          hmda: "",
          features: "",
          project_image: "",
          brochure: "",
          gallery_description: "",
          gallery_images: [],
          project_amenities: [],
          project_plan_images: [],
          approvals_compliances: [],
        });
      }
    } catch (error) {
      console.error("Error deleting project:", error);
      alert(
        `Failed to delete project: ${
          error.response?.data?.message ||
          error.response?.data?.error ||
          error.message
        }`
      );
    }
  };

  const handleSaveHome = async () => {
    if (!selectedProject?.id) return alert("No project selected.");

    const payload = { ...homeData, project_id: selectedProject.id };

    try {
      const token = localStorage.getItem("token");
      await axios.put(`${API_BASE_URL}/admin/update-project`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });
      alert("Project updated successfully!");
      window.location.reload();
    } catch (error) {
      console.error("Error saving project:", error);
      alert(
        `Failed to save project: ${
          error.response?.data?.error || error.message
        }`
      );
    }
  };

  const handleBackToList = () => {
    setSelectedProject(null);
    setActiveTab("home");
    setTabMenuOpen(false);
  };

  const renderActiveTab = () => {
    switch (activeTab) {
      case "home":
        return (
          <ProjectHomeTabForm
            homeData={homeData}
            setHomeData={setHomeData}
            onSave={handleSaveHome}
            setActiveTab={setActiveTab}
            isProject={true}
            projectId={selectedProject?.id}
          />
        );
      case "gallery":
        return (
          <ProjectGalleryTabForm
            galleryData={homeData}
            setGalleryData={setHomeData}
            onSave={handleSaveHome}
            setActiveTab={setActiveTab}
            isProject={true}
            projectId={selectedProject?.id}
          />
        );
      case "plan":
        return (
          <ProjectPlanForm
            lightboxData={homeData}
            setLightboxData={setHomeData}
            onSave={handleSaveHome}
            setActiveTab={setActiveTab}
            isProject={true}
            projectId={selectedProject?.id}
          />
        );
      case "location":
        return (
          <ProjectLocationForm
            placeholderData={homeData}
            setPlaceholderData={setHomeData}
            onSave={handleSaveHome}
            setActiveTab={setActiveTab}
            isProject={true}
            projectId={selectedProject?.id}
          />
        );
      case "amenities":
        return (
          <ProjectAmenitiesForm
            amenitiesData={homeData}
            setAmenitiesData={setHomeData}
            onSave={handleSaveHome}
            setActiveTab={setActiveTab}
            isProject={true}
            projectId={selectedProject?.id}
          />
        );
      case "approval":
        return (
          <ProjectApprovalComplianceForm
            approvalData={homeData}
            setApprovalData={setHomeData}
            onSave={handleSaveHome}
            setActiveTab={setActiveTab}
            isProject={true}
            projectId={selectedProject?.id}
          />
        );
      default:
        return null;
    }
  };

  const TabButton = ({ id, label }) => (
    <button
      onClick={() => setActiveTab(id)}
      className={`px-5 py-2 text-sm md:text-base font-semibold border-b-4 transition-all duration-300 ${
        activeTab === id
          ? "border-blue-600 text-blue-700 bg-blue-50"
          : "border-transparent text-gray-600 hover:text-blue-600 hover:bg-gray-50"
      }`}
    >
      {label}
    </button>
  );

  return (
    <div className="flex min-h-screen bg-gray-50 font-sans">
      <div className="hidden lg:block">
        <DashboardSidebar />
      </div>

      <div
        className={`fixed inset-y-0 left-0 bg-white w-72 shadow-2xl transform transition-transform duration-300 ease-in-out z-50 ${
          sidebarOpen ? "translate-x-0" : "-translate-x-full"
        } lg:hidden`}
      >
        <DashboardSidebar />
        <button
          className="absolute top-4 right-4 text-gray-500 hover:text-blue-600"
          onClick={() => setSidebarOpen(false)}
        >
          <X size={28} />
        </button>
      </div>

      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-30 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        ></div>
      )}

      <main className="flex-1 flex flex-col w-full">
        <div className="sticky top-0 bg-white z-30 shadow-sm flex items-center justify-between px-4 sm:px-8 py-4">
          <button
            onClick={() => setSidebarOpen(true)}
            className="lg:hidden text-gray-700 hover:text-blue-600"
          >
            <Menu size={28} />
          </button>
          <div className="flex-1 flex justify-center">
            <DashboardHeader
              title={
                selectedProject
                  ? `${selectedProject.name} - Project Management`
                  : "Completed Projects"
              }
              backTo="/dashboard"
            />
          </div>
        </div>

        {!selectedProject ? (
          <div className="p-10">
            <h2 className="text-2xl font-bold text-blue-700 mb-6">
              Completed Projects
            </h2>
            <div className="overflow-x-auto">
              <table className="min-w-full border border-gray-200 bg-white rounded-xl shadow-sm">
                <thead>
                  <tr className="bg-gray-100 text-left text-gray-700">
                    <th className="px-6 py-3 text-lg font-semibold">Name</th>
                    <th className="px-6 py-3 text-lg font-semibold">Status</th>
                    <th className="px-6 py-3 text-lg font-semibold">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {projects.map((project) => {
                    const id = project.project_id || project.id;
                    const name =
                      project.project_title ||
                      project.project_name ||
                      project.name ||
                      "Untitled Project";
                    const status =
                      project.status || project.project_status;

                    return (
                      <tr
                        key={id}
                        className="border-t hover:bg-gray-50 transition"
                      >
                        <td className="px-6 py-4 text-gray-800 text-lg font-medium">
                          {name}
                        </td>
                        <td className="px-6 py-4">
                          {typeof status === "number" ? (
                            <span
                              className={`text-white text-sm px-4 py-2 rounded-lg font-semibold ${
                                status === 100
                                  ? "bg-green-600"
                                  : status >= 70
                                  ? "bg-yellow-500"
                                  : "bg-gray-400"
                              }`}
                            >
                              {status} %
                            </span>
                          ) : status ? (
                            <span className="inline-flex px-4 py-2 rounded-lg bg-green-100 text-green-700 text-sm font-semibold">
                              {status}
                            </span>
                          ) : (
                            <span className="text-gray-400 text-sm">
                              N/A
                            </span>
                          )}
                        </td>
                        <td className="px-6 py-4 flex items-center space-x-4">
                          <button
                            onClick={() => handleEditProject(project)}
                            className="text-gray-700 hover:text-blue-600 transition"
                          >
                            <Edit3 size={22} />
                          </button>
                          <button
                            onClick={() => handleDeleteProject(id)}
                            className="text-red-500 hover:text-red-700 transition"
                          >
                            <Trash2 size={22} />
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        ) : (
          <div className="p-4 sm:p-8 md:p-10 max-w-6xl mx-auto w-full bg-white mt-4 sm:mt-6 rounded-xl shadow-md">
            <div className="flex justify-start mb-8">
              <button
                onClick={handleBackToList}
                className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg shadow transition"
              >
                <ArrowLeft size={18} />
                Back to Projects
              </button>
            </div>

            <div className="bg-white border-b border-gray-200 shadow-sm mb-6">
              <div className="px-4 py-2 flex items-center justify-between lg:hidden">
                <span className="font-semibold text-gray-800 text-sm">
                  {activeTabLabel}
                </span>
                <button
                  onClick={() => setTabMenuOpen((prev) => !prev)}
                  className="flex items-center gap-2 px-3 py-2 text-sm border rounded-lg text-gray-700 hover:bg-gray-50"
                >
                  <span>Sections</span>
                  <Menu size={18} />
                </button>
              </div>

              {tabMenuOpen && (
                <div className="px-4 pb-3 space-y-1 lg:hidden">
                  {tabs.map((tab) => (
                    <button
                      key={tab.id}
                      onClick={() => {
                        setActiveTab(tab.id);
                        setTabMenuOpen(false);
                      }}
                      className={`w-full text-left px-3 py-2 rounded-md text-sm font-medium ${
                        activeTab === tab.id
                          ? "bg-blue-50 text-blue-700"
                          : "text-gray-700 hover:bg-gray-50"
                      }`}
                    >
                      {tab.label}
                    </button>
                  ))}
                </div>
              )}

              <div className="hidden lg:flex justify-center gap-2 sm:gap-4 px-2 sm:px-4 py-2 flex-wrap">
                {tabs.map((tab) => (
                  <TabButton key={tab.id} id={tab.id} label={tab.label} />
                ))}
              </div>
            </div>

            {renderActiveTab()}
          </div>
        )}
      </main>
    </div>
  );
};

export default CompletedProjectPage;
